import {ExternalLinkIcon} from '@heroicons/react/outline';
import {isEmpty} from 'lodash-es';
import {Link, useParams} from 'react-router-dom';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Button, Header, SpinningLoader} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import {AfriexKYCProcessor, AfriexVerificationStatus} from '@/types';

import KYCIDPrimary from './KYCIDPrimary';
import KYCIDSecondary from './KYCIDSecondary';
import KYCRequestedDcuments from './KYCRequestedDocuments';
import KYCUpdateButton from './KYCUpdateButton';
import KYCVerificationView from './KYCVerificationView';
import Summary from './Summary';
import useKYCDetails from './useKYCDetails';
import VirtualAccountApplicationView from './VirtualAccountApplicationView';
import VirtualAccountInfoView from './VirtualAccountInfoView';

const KYCDetails = () => {
  const params = useParams();
  const userId = params?.userId || '';
  const {
    isLoadingUserDetails,
    handleVerify,
    handleSubmission,
    handleDeleteSession,
    user,
    virtualAccountApplication,
    virtualAccountInfo,
  } = useKYCDetails(userId);
  const isVerified = user?.kyc?.status === AfriexVerificationStatus.Success;
  const isKYCUploaded = Boolean(user?.kyc?.primary?.idNumber);
  const verifyLabel = isVerified ? 'Unverify' : 'Mark as verified';
  const processors = Object.values(AfriexKYCProcessor);
  const hasTooManyRetries = processors.some(
    (processor) => (user?.kyc?.verification?.[processor]?.attempts ?? 0) >= 1,
  );
  const hasKycImages =
    (Object.keys(user?.kyc?.images ?? 0)?.length ?? 0) > 0 ||
    (user?.kyc?.media?.images?.length ?? 0) > 0 ||
    (user?.kyc?.docs?.length ?? 0) > 0;

  if (isLoadingUserDetails) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <SpinningLoader />
      </div>
    );
  }

  return (
    <div className="mt-10 mb-5 mx-4 md:mx-10">
      <Header title={`${user.name?.fullName || ''} KYC Details`} />

      {/* Top Buttons */}
      <div className="w-full p-3 flex gap-2 justify-between">
        <div className="flex flex-col md:flex-row gap-2">
          <PermissionsProvider permission={AfriexAdminPermission.KYCUpdate}>
            <Button
              colorScheme="cyan"
              variant="outline"
              onClick={() => handleVerify(!isVerified)}
            >
              {verifyLabel}
            </Button>
          </PermissionsProvider>

          <PermissionsProvider permission={AfriexAdminPermission.KYCUpdate}>
            <KYCUpdateButton user={user} isKYCUploaded />
          </PermissionsProvider>

          <PermissionsProvider
            permission={AfriexAdminPermission.KYCVerificationCreate}
          >
            <Button
              disabled={!isKYCUploaded || isVerified || hasTooManyRetries}
              colorScheme="cyan"
              variant="outline"
              onClick={() => {
                // Fire-and-forget: handleSubmission try/catches, clears
                // `isLoadingUserDetails` and reports through
                // showErrorMessage / showSuccessMessage, so this only stops an
                // unexpected throw becoming an unhandled rejection.
                handleSubmission(AfriexKYCProcessor.Veriff).catch(() => {});
                return false;
              }}
            >
              Retry Veriff Verification
            </Button>
          </PermissionsProvider>
        </div>

        <div>
          {hasKycImages && (
            <Link
              to={`/kyc/${params?.userId}/receipt`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                colorScheme="cyan"
                variant="ghost"
                // className="bg-indigo-200 dark:bg-cyan-700 border-none dark:border-none text-cyan-700 dark:text-white"
                leftIcon={<ExternalLinkIcon className="w-4 h-4" />}
              >
                View KYC Download
              </Button>
            </Link>
          )}
        </div>
      </div>

      <KYCRequestedDcuments user={user} />

      <Summary user={user} />

      <KYCIDPrimary user={user} />

      {user?.kyc?.secondary?.idNumber && <KYCIDSecondary user={user} />}

      {/* Virtual Account Information */}
      {!isEmpty(virtualAccountApplication) && (
        <VirtualAccountApplicationView data={virtualAccountApplication} />
      )}

      {/* US -> UK - EU */}
      {!isEmpty(virtualAccountInfo) && (
        <VirtualAccountInfoView data={virtualAccountInfo} />
      )}

      {Object.keys(user?.kyc?.verification ?? {})?.map((processor, idx) => (
        <KYCVerificationView
          key={idx}
          processor={processor as AfriexKYCProcessor}
          handleDeleteSession={handleDeleteSession}
          // The keys come straight off `verification`, so the cast recovers
          // the key type `Object.keys` widens to `string`.
          verification={
            user?.kyc?.verification?.[processor as AfriexKYCProcessor] ?? {}
          }
        />
      ))}
    </div>
  );
};

export default KYCDetails;
